import type { TeamTactics } from '../useGameStore';
import { DEFAULT_TEAM_TACTICS } from './constants';
import { getRotationLabelFromCourtRoles, isP1SideOutSwapFor } from '../../utils/attackPassTypes';
import { hasTwoSetters, playingRoles } from '../../utils/gameSystem';

interface CourtRolesTeam {
  court?: readonly string[];
  courtRoles: readonly string[];
  playerRoles?: Readonly<Record<string, string>>;
}

/**
 * Règles d'équipe lues POUR UN CÔTÉ — la nôtre ou celle d'en face.
 *
 * Les écrans de saisie lisaient `teamTactics` et `isP1SwapActive` sans dire de
 * quelle équipe il s'agissait : c'étaient ceux de NOTRE équipe, appliqués à
 * l'adversaire dès qu'on le saisissait ou qu'on le dessinait. Sa tactique
 * (réglable pourtant) et sa rotation P1 n'étaient donc jamais prises en compte.
 *
 * Fonctions pures, sans import du store : le store s'en sert lui-même.
 */
/** Tactique de l'équipe `side` : la sienne, pas la nôtre par défaut. */
export function tacticsFor(
  state: { teamTactics: TeamTactics; opponentTeamTactics?: TeamTactics },
  side: 'us' | 'them',
): TeamTactics {
  return side === 'them' ? state.opponentTeamTactics ?? DEFAULT_TEAM_TACTICS : state.teamTactics;
}

interface SubstitutionLike {
  set: number;
  action: string;
  player: string;
  zone?: string;
  isOpponent?: boolean;
}

interface LiveTacticsState {
  teamTactics: TeamTactics;
  opponentTeamTactics?: TeamTactics;
  currentSet: number;
  // Optional: older persisted states and partial test stores may lack them.
  history?: readonly SubstitutionLike[];
  currentPointEvents?: readonly SubstitutionLike[];
  teams: Partial<Record<'us' | 'them', { roster: readonly { id: string; name: string }[] }>>;
}

/**
 * Roster ids of the players who came in, this set, for a player the libero
 * replaces (or for one of their own substitutes): they inherit the choice
 * (VOL-162). Derived from the set's substitutions, so resume, undo and history
 * edits stay right without any stored state.
 */
export function liberoStandInIds(
  chosenIds: readonly string[],
  roster: readonly { id: string; name: string }[],
  setEvents: readonly SubstitutionLike[],
  side: 'us' | 'them',
): string[] {
  if (chosenIds.length === 0) return [];
  const chosen = new Set(roster.filter((p) => chosenIds.includes(p.id)).map((p) => p.name));
  const standIns = new Set<string>();
  for (const e of setEvents) {
    if (e.action !== 'Substitution' || !e.zone?.startsWith('OUT:')) continue;
    if ((side === 'them') !== !!e.isOpponent) continue;
    const out = e.zone.slice(4);
    if (chosen.has(out) || standIns.has(out)) standIns.add(e.player);
    standIns.delete(out);
  }
  return roster.filter((p) => standIns.has(p.name) && !chosenIds.includes(p.id)).map((p) => p.id);
}

/** `tacticsFor`, plus the substitutes who inherit « Le libéro remplace » this set. */
export function liveTacticsFor(state: LiveTacticsState, side: 'us' | 'them'): TeamTactics {
  const tactics = tacticsFor(state, side);
  const chosen = tactics?.liberoReplaces ?? [];
  if (chosen.length === 0) return tactics;
  const setEvents = [
    ...(state.history ?? []).filter((e) => e.set === state.currentSet),
    ...(state.currentPointEvents ?? []),
  ];
  const standIns = liberoStandInIds(chosen, state.teams[side]?.roster ?? [], setEvents, side);
  return standIns.length > 0 ? { ...tactics, liberoReplaces: [...chosen, ...standIns] } : tactics;
}

/**
 * Permutation P1 de l'équipe `side`.
 *
 * Pour NOUS, on garde le drapeau du store tel quel : il est posé au début de
 * chaque rallye et c'est le comportement éprouvé du mode solo. Pour
 * l'ADVERSAIRE, même règle, dérivée de sa propre rotation : en P1 et en
 * réception.
 *
 * In a 4-2 or 6-2 (VOL-162) the rule follows the setter of the rally, not the
 * designated one: P1 is where the player who sets this rally stands.
 */
export function p1SwapFor(
  state: {
    isP1SwapActive: boolean;
    servingSide: 'us' | 'them';
    teams: Partial<Record<'us' | 'them', CourtRolesTeam>> & { them: { courtRoles: readonly string[] } };
    teamTactics?: TeamTactics;
    opponentTeamTactics?: TeamTactics;
  },
  side: 'us' | 'them',
): boolean {
  const system = side === 'them' ? state.opponentTeamTactics?.gameSystem : state.teamTactics?.gameSystem;
  const team = state.teams[side];
  if (hasTwoSetters(system) && team?.court && team.playerRoles) {
    const roles = playingRoles({ court: team.court, courtRoles: team.courtRoles, playerRoles: team.playerRoles }, system);
    if (!roles.includes('Passeur')) return false;
    return isP1SideOutSwapFor(side, getRotationLabelFromCourtRoles([...roles]), state.servingSide);
  }
  if (side === 'us') return state.isP1SwapActive;
  const roles = state.teams.them.courtRoles;
  // Sans passeur désigné, pas de rotation connue : pas de permutation.
  if (!roles.includes('Passeur')) return false;
  return isP1SideOutSwapFor('them', getRotationLabelFromCourtRoles([...roles]), state.servingSide);
}
